/**
 * Kiosk mode hardening for Horizon.
 *
 * - Locks browsing to the kiosk collection/landing page/product pages while active.
 * - Hides the real checkout button and shows a QR code (rendered via the
 *   api.qrserver.com image API - cart contents are sent to that third-party
 *   service to generate the image) that lets the shopper finish checkout on
 *   their own device instead of the kiosk.
 * - Clears the cart automatically after an inactivity timeout, and whenever a new
 *   kiosk session starts (new tab, or the previous session went idle for a while),
 *   so one shopper's cart can never leak into the next shopper's session.
 * - Validates the `?rebuild=` cart-handoff parameter before touching the cart.
 */
(function () {
  'use strict';

  var STORAGE_KEYS = {
    mode: 'kiosk_mode',
    sessionStarted: 'kiosk_session_started'
  };

  var POLL_INTERVAL_MS = 4000;

  var root = document.documentElement;
  var kioskCollectionUrl = '';
  var kioskPageUrl = '';
  var kioskCartTimerMinutes = 5;

  function getBody() {
    return document.body || document.querySelector('body');
  }

  var body = getBody();
  if (body) {
    kioskCollectionUrl = body.dataset.kioskCollectionUrl || '';
    kioskPageUrl = body.dataset.kioskPageUrl || '';
    kioskCartTimerMinutes = parseInt(body.dataset.kioskCartTimer || '', 10);
  }
  if (!kioskCartTimerMinutes || kioskCartTimerMinutes <= 0) {
    kioskCartTimerMinutes = 5;
  }

  function resolveKioskNavigationLock() {
    var storedValue = readLocal('kiosk_lock_navigation');
    if (storedValue === 'true' || storedValue === 'false') {
      return storedValue === 'true';
    }

    var value = (body && body.dataset.kioskLockNavigation) || 'false';
    return value !== 'false' && value !== '0' && value !== 'no';
  }

  function isKioskNavigationLocked() {
    return resolveKioskNavigationLock();
  }

  var timerInterval = null;
  var timerEndsAt = null;
  // Decided once, synchronously, before init() kicks off any async work
  // (fetches). See maybeClearCartForNewSession() for why this can't be
  // decided lazily inside an async callback.
  var pendingNewSessionClear = false;

  function readLocal(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeLocal(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable, kiosk mode falls back to a no-op */
    }
  }

  function removeLocal(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {}
  }

  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function isKioskModeActive() {
    return readLocal(STORAGE_KEYS.mode) === '1';
  }

  function applyKioskModeFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var value = params.get('kiosk');
    var currentMode = readLocal(STORAGE_KEYS.mode);

    if (value === '1') {
      writeLocal(STORAGE_KEYS.mode, '1');
      return;
    }

    if (value === '0') {
      writeLocal(STORAGE_KEYS.mode, '0');
      return;
    }

    if (currentMode === null) {
      writeLocal(STORAGE_KEYS.mode, '0');
    }
  }

  function syncModeClass() {
    var active = isKioskModeActive();
    var bodyEl = getBody();
    var lockEnabled = isKioskNavigationLocked();

    root.classList.toggle('kiosk-mode', active);
    if (bodyEl) bodyEl.classList.toggle('kiosk-mode', active);

    if (bodyEl) {
      bodyEl.setAttribute('data-kiosk-lock-navigation', String(lockEnabled));
      root.setAttribute('data-kiosk-lock-navigation', String(lockEnabled));
    }

    var hiddenSelectors = 'shopify-account, .account-button.header-actions__action, .header-actions__action:not(.action__cart):not(.search-action .header-actions__action):not([data-testid="cart-drawer-trigger"]), .accelerated-checkout-block, .additional-checkout-buttons, shopify-accelerated-checkout, .shopify-payment-button, footer';
    document.querySelectorAll(hiddenSelectors).forEach(function (el) {
      el.style.display = active ? 'none' : '';
    });

    document.querySelectorAll('header-menu').forEach(function (el) {
      el.style.display = lockEnabled ? 'none' : '';
    });

    syncMenuItemVisibility();
  }

  // "About us" and "Contact us" are handled as explicit, unconditional cases
  // rather than falling through the generic public/non-public guess: About
  // must stay visible even when navigation lock hides everything else, and
  // Contact must stay hidden even if a future edit loosens the generic
  // allow-list. Both are checked by href first (stable) and menu text as a
  // fallback (merchant-editable, so it's a fallback, not primary).
  function isAboutMenuItem(href, text) {
    return href.indexOf('/pages/about') !== -1 || text.indexOf('about') !== -1;
  }

  function isContactMenuItem(href, text) {
    return href.indexOf('/pages/contact') !== -1 || href.indexOf('/contact') !== -1 || text.indexOf('contact') !== -1;
  }

  function isBlockedKioskLink(href, text) {
    var lowerHref = (href || '').toLowerCase();
    var lowerText = (text || '').toLowerCase();

    if (!lowerHref && !lowerText) return false;
    if (lowerHref.indexOf('mailto:') === 0 || lowerHref.indexOf('tel:') === 0) return true;
    if (lowerHref.indexOf('northumbria.ac.uk') !== -1 || lowerText.indexOf('northumbria university') !== -1) return true;
    if (lowerHref.indexOf('/pages/contact') !== -1 || lowerHref.indexOf('/contact') !== -1 || lowerText.indexOf('contact') !== -1) return true;
    if (lowerHref.indexOf('/blogs') !== -1 || lowerHref.indexOf('/blog') !== -1 || lowerText.indexOf('blog') !== -1) return true;
    if (lowerText.indexOf('follow') !== -1 || lowerText.indexOf('instagram') !== -1 || lowerText.indexOf('facebook') !== -1 || lowerText.indexOf('twitter') !== -1 || lowerText.indexOf('social') !== -1) return true;
    if (lowerHref.indexOf('instagram.com') !== -1 || lowerHref.indexOf('facebook.com') !== -1 || lowerHref.indexOf('twitter.com') !== -1 || lowerHref.indexOf('x.com') !== -1 || lowerHref.indexOf('linkedin.com') !== -1 || lowerHref.indexOf('youtube.com') !== -1 || lowerHref.indexOf('tiktok.com') !== -1 || lowerHref.indexOf('pinterest.com') !== -1 || lowerHref.indexOf('snapchat.com') !== -1) return true;
    if (lowerHref.indexOf('http://') === 0 || lowerHref.indexOf('https://') === 0 || lowerHref.indexOf('//') === 0) {
      try {
        var parsed = new URL(lowerHref, window.location.href);
        return parsed.origin !== window.location.origin;
      } catch (e) {
        return true;
      }
    }

    return false;
  }

  function syncExternalLinkVisibility() {
    var active = isKioskModeActive();
    if (!active) return;

    document.querySelectorAll('.footer-block, .footer__block, footer section, footer .footer-column, .social-links, .social, a[href]').forEach(function (node) {
      var text = (node.textContent || '').toLowerCase();
      if ((node.tagName === 'A' || node.matches('a')) && (node.getAttribute('href') || '').toLowerCase().indexOf('northumbria.ac.uk') !== -1) {
        node.style.display = 'none';
        return;
      }

      if (text.indexOf('visit us') !== -1 || text.indexOf('follow us') !== -1 || text.indexOf('follow on shop') !== -1) {
        node.style.display = 'none';
        return;
      }

      if (node.matches && node.matches('a[href]')) {
        var href = node.getAttribute('href') || '';
        var hrefText = node.textContent || '';

        if (isBlockedKioskLink(href, hrefText)) {
          var parent = node.closest('li, .list-menu__item, .menu-list__list-item, .social, .footer-block');
          if (parent) parent.style.display = 'none';
          node.style.display = 'none';
        } else if (node.style.display === 'none') {
          node.style.display = '';
        }
      }
    });
  }

  function syncOverflowMenuState() {
    var visibleCount = 0;
    document.querySelectorAll('header-menu .menu-list__list-item, header-menu li, nav .menu-list__list-item, nav li').forEach(function (item) {
      if (item.getAttribute('slot') === 'overflow') return;
      if (item.style.display === 'none') return;
      if (item.querySelector('a')) visibleCount += 1;
    });

    document.querySelectorAll('overflow-list').forEach(function (overflowList) {
      var shadowRoot = overflowList.shadowRoot;
      if (!shadowRoot) return;

      var moreSlot = shadowRoot.querySelector('slot[name="more"]');
      var overflowPanel = shadowRoot.querySelector('div[part="overflow"]');
      var allowOverflow = visibleCount > 0;

      if (moreSlot) {
        moreSlot.hidden = !allowOverflow;
      }

      if (overflowPanel) {
        overflowPanel.style.display = allowOverflow ? '' : 'none';
      }
    });
  }

  function syncMenuItemVisibility() {
    var active = isKioskModeActive();
    document.querySelectorAll('header-menu .menu-list__list-item, header-menu li, nav .menu-list__list-item, nav li').forEach(function (item) {
      var anchor = item.querySelector('a');
      if (!anchor) return;
      var href = (anchor.getAttribute('href') || '').toLowerCase();
      var text = (anchor.textContent || '').trim().toLowerCase();

      if (active && isBlockedKioskLink(href, text)) {
        item.style.display = 'none';
        return;
      }

      if (active && isAboutMenuItem(href, text)) {
        item.style.display = '';
        return;
      }

      if (active && isContactMenuItem(href, text)) {
        item.style.display = 'none';
        return;
      }

      var isPublicNav = href === '/' || href.indexOf('/collections') !== -1 || href.indexOf('/catalog') !== -1 || href.indexOf('/products') !== -1 || text.indexOf('home') !== -1 || text.indexOf('catalog') !== -1 || href.indexOf('/search') !== -1 || text.indexOf('search') !== -1;
      if (active && !isPublicNav) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
      }
    });

    syncOverflowMenuState();
    syncExternalLinkVisibility();
  }

  // Horizon's header-menu component can rebuild its own list markup after
  // load (mega-menu construction, mobile-drawer cloning, breakpoint changes),
  // which wipes any inline style we already applied and lets a hidden item
  // flash back into view. Re-running the filter whenever that subtree
  // changes closes that gap instead of relying on a single pass at init.
  var menuObserver = null;

  function observeMenuChanges() {
    if (menuObserver || typeof MutationObserver === 'undefined') return;
    menuObserver = new MutationObserver(function () {
      if (!isKioskModeActive()) return;
      syncMenuItemVisibility();
    });
    document.querySelectorAll('header-menu, nav').forEach(function (navEl) {
      menuObserver.observe(navEl, { childList: true, subtree: true });
    });
  }

  function isUnsafeKioskPath(pathname) {
    return (
      pathname.indexOf('/account') === 0 ||
      pathname.indexOf('/checkout') === 0 ||
      pathname.indexOf('/checkouts') === 0 ||
      pathname.indexOf('/contact') === 0 ||
      pathname.indexOf('/pages/contact') === 0
    );
  }

  function isAllowedKioskPath(pathname) {
    if (!kioskPageUrl && !kioskCollectionUrl) return true;
    if (pathname.indexOf('/cart') === 0) return true;
    if (pathname.indexOf('/products/') === 0) return true;

    if (!isKioskNavigationLocked()) {
      return true;
    }

    if (kioskPageUrl && pathname === kioskPageUrl) return true;
    if (kioskCollectionUrl && pathname.indexOf(kioskCollectionUrl) === 0) return true;
    return false;
  }

  function enforceNavigationLock() {
    if (!isKioskModeActive()) return;
    var pathname = window.location.pathname;
    var redirectTo = kioskPageUrl || kioskCollectionUrl || '/';

    if (isUnsafeKioskPath(pathname)) {
      window.location.replace(redirectTo);
      return;
    }

    if (!isKioskNavigationLocked()) return;
    if (!kioskPageUrl && !kioskCollectionUrl) return;
    if (isAllowedKioskPath(pathname)) return;

    window.location.replace(redirectTo);
  }

  function recordActivity() {
    if (!isKioskModeActive()) return;
    if (!timerInterval) return;

    timerEndsAt = Date.now() + kioskCartTimerMinutes * 60 * 1000;
    updateTimerDisplay();
  }

  function parseRebuildItems(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map(function (pair) {
        var parts = pair.split(':');
        return {
          id: parseInt(parts[0], 10),
          quantity: parseInt(parts[1], 10)
        };
      })
      .filter(function (item) {
        return (
          Number.isInteger(item.id) &&
          item.id > 0 &&
          Number.isInteger(item.quantity) &&
          item.quantity > 0 &&
          item.quantity <= 100
        );
      })
      .slice(0, 50);
  }

  function rebuildCartFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var raw = params.get('rebuild');
    if (!raw) return;
    var items = parseRebuildItems(raw);
    if (!items.length) return;

    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function () {
        pollCartStatus();
      })
      .catch(function () {});
  }

  function maybeClearCartForNewSession(cart) {
    var shouldClear = pendingNewSessionClear;
    // Only ever attempt the "new session" clear once. `pendingNewSessionClear`
    // is decided synchronously at init(), before any fetch, specifically so a
    // shopper who adds to cart quickly after the page loads can't race ahead
    // of us and get their item wiped by this check on a later poll tick.
    pendingNewSessionClear = false;

    if (!isKioskModeActive() || cart.item_count === 0 || !shouldClear) {
      return Promise.resolve(cart);
    }

    return fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return cart;
      });
  }

  function stopTimer() {
    if (timerInterval) {
      window.clearInterval(timerInterval);
      timerInterval = null;
    }
    timerEndsAt = null;
    document.querySelectorAll('.kiosk-cart-timer').forEach(function (el) {
      el.textContent = '';
    });
  }

  function clearCartAndRefresh() {
    // A direct fetch() here empties the cart server-side, but Horizon's own
    // cart-drawer/cart-page components only re-render their line-item markup
    // in response to their own add/update/remove flow - not to an unrelated
    // fetch from this script. Polling afterwards (pollCartStatus) correctly
    // hides the QR code and stops the timer, but the stale line items stay
    // on screen until something forces a real reload of the cart section.
    // Navigating back to the kiosk landing page guarantees the stale cart
    // view can never linger, and doubles as a clean reset for the next
    // shopper, matching the reset that already happens at a new session.
    var redirectTo = kioskPageUrl || kioskCollectionUrl || '/';
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function () {
        window.location.replace(redirectTo);
      })
      .catch(function () {
        window.location.replace(redirectTo);
      });
  }

  function updateTimerDisplay() {
    if (!timerEndsAt) return;
    var remainingMs = timerEndsAt - Date.now();
    if (remainingMs <= 0) {
      stopTimer();
      clearCartAndRefresh();
      return;
    }
    var totalSeconds = Math.ceil(remainingMs / 1000);
    var minutes = Math.floor(totalSeconds / 60);
    var seconds = totalSeconds % 60;
    var label = 'Cart clears in ' + minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
    document.querySelectorAll('.kiosk-cart-timer').forEach(function (el) {
      el.textContent = label;
    });
  }

  function startTimer() {
    if (timerInterval) return;
    timerEndsAt = Date.now() + kioskCartTimerMinutes * 60 * 1000;
    updateTimerDisplay();
    timerInterval = window.setInterval(updateTimerDisplay, 1000);
  }

  function buildCartQrIdentifier(cart) {
    if (!cart.items || !cart.items.length) return '';
    var pairs = cart.items
      .map(function (item) {
        return item.variant_id + ':' + item.quantity;
      })
      .join(',');
    // Tag the resulting order as kiosk-sourced via cart permalink query params.
    // Shopify applies `note`/`attributes[...]` server-side before redirecting,
    // which matters here because a drawer cart_type sends this permalink
    // straight to checkout - the shopper's device never loads our theme's JS.
    var params = new URLSearchParams();
    params.set('kiosk', '0');
    params.set('attributes[Order Source]', 'kiosk');
    params.set('note', 'Kiosk order (QR handoff)');
    return window.location.origin + '/cart/' + pairs + '?' + params.toString();
  }

  function refreshQrCode(cart) {
    var identifier = buildCartQrIdentifier(cart);
    var qrUrl = identifier ? 'https://api.qrserver.com/v1/create-qr-code/?data=' + encodeURIComponent(identifier) + '&size=160x160' : '';
    document.querySelectorAll('#Kiosk-QR-Code img[data-kiosk-qr], #Cart-QR-Code img[data-kiosk-qr]').forEach(function (el) {
      if (el.getAttribute('src') === qrUrl) return;
      el.setAttribute('src', qrUrl);
      el.setAttribute('alt', 'QR code to complete checkout on your own device');
    });
  }

  function applyCartStatus(cart) {
    var active = isKioskModeActive();
    var cartIsEmpty = cart.item_count === 0;

    document.querySelectorAll('#Kiosk-QR-Code, #Cart-QR-Code').forEach(function (el) {
      el.style.display = active && !cartIsEmpty ? 'flex' : 'none';
    });

    if (active && !cartIsEmpty) {
      refreshQrCode(cart);
      startTimer();
    } else {
      stopTimer();
    }
  }

  function pollCartStatus() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return maybeClearCartForNewSession(cart).then(function (updatedCart) {
          return updatedCart && typeof updatedCart.item_count === 'number' ? updatedCart : cart;
        });
      })
      .then(function (cart) {
        // Always re-apply: Horizon re-renders the cart section from the
        // server (via its own section-morph flow) after every add/update,
        // which recreates the QR <img> with a blank src. That can happen
        // after this poll already saw the same cart contents, so skipping
        // on "no change" would leave the QR code stuck blank until the
        // next real cart change (or a full page reload).
        applyCartStatus(cart);
      })
      .catch(function () {});
  }

  // The regular poll is the compatibility fallback when a Horizon version
  // does not emit this native event.
  document.addEventListener('cart:update', function () {
    window.setTimeout(pollCartStatus, 300);
  });

  function init() {
    applyKioskModeFromUrl();
    syncModeClass();
    observeMenuChanges();
    recordActivity();
    enforceNavigationLock();
    // Must happen synchronously, before rebuildCartFromUrl()/pollCartStatus()
    // touch the network, so a shopper adding to cart quickly after the page
    // loads can never race ahead of this decision (see
    // maybeClearCartForNewSession()).
    pendingNewSessionClear = readSession(STORAGE_KEYS.sessionStarted) !== '1';
    writeSession(STORAGE_KEYS.sessionStarted, '1');
    rebuildCartFromUrl();
    pollCartStatus();
    window.setInterval(pollCartStatus, POLL_INTERVAL_MS);
    document.addEventListener('click', recordActivity);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();